import { useState, useRef, useCallback, useEffect } from 'react';

interface UseVideoPlayerOptions {
  onTimeUpdate?: (currentTime: number, currentFrame: number) => void;
  onFrameChange?: (frame: number) => void;
  fps?: number;
}

export function useVideoPlayer(options: UseVideoPlayerOptions = {}) {
  const { onTimeUpdate, onFrameChange, fps = 30 } = options;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isReady, setIsReady] = useState(false);

  // Handle time updates
  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;

    const time = videoRef.current.currentTime;
    const frame = Math.floor(time * fps);

    setCurrentTime(time);
    setCurrentFrame(frame);
    onTimeUpdate?.(time, frame);

    if (frame !== currentFrame) {
      onFrameChange?.(frame);
    }
  }, [fps, onTimeUpdate, onFrameChange, currentFrame]);

  // Handle metadata loaded
  const handleLoadedMetadata = useCallback(() => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
    setIsReady(true);
  }, []);

  // Playback controls
  const play = useCallback(async () => {
    if (!videoRef.current) return;
    try {
      await videoRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Error playing video:', error);
    }
  }, []);

  const pause = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    setIsPlaying(false);
  }, []);

  const togglePlay = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  const seek = useCallback((time: number) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, Math.min(time, duration));
  }, [duration]);

  const seekToFrame = useCallback((frame: number) => {
    // Add sub-frame epsilon to prevent floating-point truncation
    // in Math.floor(currentTime * fps) from landing one frame short
    const time = (frame + 0.001) / fps;
    seek(time);
  }, [fps, seek]);

  const nextFrame = useCallback(() => {
    seekToFrame(currentFrame + 1);
  }, [currentFrame, seekToFrame]);

  const previousFrame = useCallback(() => {
    seekToFrame(currentFrame - 1);
  }, [currentFrame, seekToFrame]);

  const reset = useCallback(() => {
    seek(0);
    pause();
  }, [seek, pause]);

  // Setup event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', () => setIsPlaying(true));
    video.addEventListener('pause', () => setIsPlaying(false));
    video.addEventListener('ended', () => setIsPlaying(false));

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', () => setIsPlaying(true));
      video.removeEventListener('pause', () => setIsPlaying(false));
      video.removeEventListener('ended', () => setIsPlaying(false));
    };
  }, [handleTimeUpdate, handleLoadedMetadata]);

  return {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    currentFrame,
    totalFrames: Math.floor(duration * fps),
    isReady,
    play,
    pause,
    togglePlay,
    seek,
    seekToFrame,
    nextFrame,
    previousFrame,
    reset,
  };
}
